import React, { useEffect, useMemo, useState } from 'react';
import { getEffectiveStatus, getStatusLabel, isAwaitingReview } from '../utils/status';
import {
  getLanguagePairLabel,
  getTmDisplayName,
  resolveTmId,
} from '../utils/languages';
import { isRequestModified } from './ModifiedBadge';
import SpanishCellPopover from './SpanishCellPopover';
import StatusCell from './StatusCell';
import TranslationLockControl from './TranslationLockControl';
import TruncatedCopyCell from './TruncatedCopyCell';
import { normalizeLockState } from '../utils/translationLock';

const PAGE_SIZE = 15;

// Comparator for any string field: case-insensitive, with empty
// values pushed to the bottom regardless of direction so blanks
// do not crowd the visible window when sorting.
function compareStrings(a, b, dir) {
  const aBlank = !a;
  const bBlank = !b;
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;
  const cmp = a.toString().toLowerCase().localeCompare(b.toString().toLowerCase());
  return dir === 'asc' ? cmp : -cmp;
}

function compareDates(a, b, dir) {
  const ta = a ? new Date(a).getTime() : 0;
  const tb = b ? new Date(b).getTime() : 0;
  return dir === 'asc' ? ta - tb : tb - ta;
}

function getWordCount(request) {
  if (request.source_word_count != null) return Number(request.source_word_count);
  const src = request.source || request.English || '';
  return src ? src.split(/\s+/).filter(Boolean).length : 0;
}

function sortValue(request, key, availableTMs = []) {
  switch (key) {
    case 'source':
      return request.source || request.English || '';
    case 'target':
      return request.target || request.Spanish || '';
    case 'translation_type':
      return request.translation_type || '';
    case 'words':
      return getWordCount(request);
    case 'status':
      return getStatusLabel(getEffectiveStatus(request));
    case 'received_date':
      return request.received_date || '';
    case 'language_pair':
      return getLanguagePairLabel(request, true);
    case 'tm_id':
      return getTmDisplayName(resolveTmId(request, availableTMs), availableTMs);
    default:
      return '';
  }
}

function RequestsTable({
  requests,
  loading,
  onRefresh,
  onViewRequest,
  lastRefresh,
  title = 'My translations - Sections',
  subtitle = 'Saved to your account and available after you sign back in',
  emptyMessage = 'No translation requests yet.',
  emptyHint = 'Submit a translation request to get started.',
  // Optional helper copy rendered next to the card title (right side of header).
  headerHelper = null,
  // Layout mode toggles:
  // - showActions: whether to render the per-row Review button
  // - showComments: whether to render the linguist comments column
  showActions = true,
  showComments = false,
  availableTMs = [],
  currentUserId = '',
  onLockStatusChange,
  onNotify,
}) {
  const [page, setPage] = useState(0);
  // Sort state: defaults to most-recent-first by received_date so the
  // table preserves its original ordering until the user picks a
  // column. Click cycles asc -> desc -> back to default (date desc).
  const [sortKey, setSortKey] = useState('received_date');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    // Third click: revert to default ordering.
    setSortKey('received_date');
    setSortDir('desc');
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return <span className="sort-indicator sort-indicator--idle">↕</span>;
    return (
      <span className="sort-indicator sort-indicator--active">
        {sortDir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const getTypeClass = (type) => {
    switch (type?.toLowerCase()) {
      case 'exact':
        return 'type-exact';
      case 'fuzzy-adapted':
      case 'fuzzy':
        return 'type-fuzzy-adapted';
      case 'custom':
        return 'type-custom';
      default:
        return '';
    }
  };

  const formatTranslationType = (type) => {
    if (!type) return '-';
    return type.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('-');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // A row is "reviewable" when the AI translation is done (including
  // previously approved rows, which linguists can re-edit).
  const canReview = (request) => {
    const hasTranslation = !!(request.target || request.Spanish);
    return hasTranslation && isAwaitingReview(request);
  };

  // Apply user-controlled sort. Date column uses numeric comparison;
  // everything else falls through to the case-insensitive string
  // comparator, with blanks always pushed to the bottom.
  const sortedRequests = useMemo(() => {
    const safe = Array.isArray(requests) ? [...requests] : [];
    safe.sort((a, b) => {
      if (sortKey === 'received_date') {
        return compareDates(a.received_date, b.received_date, sortDir);
      }
      if (sortKey === 'words') {
        const diff = getWordCount(a) - getWordCount(b);
        return sortDir === 'asc' ? diff : -diff;
      }
      return compareStrings(
        sortValue(a, sortKey, availableTMs),
        sortValue(b, sortKey, availableTMs),
        sortDir,
      );
    });
    return safe;
  }, [requests, sortKey, sortDir, availableTMs]);

  const columnLabels = useMemo(() => ({
    source: 'Source',
    target: 'Translated',
  }), []);

  const totalPages = Math.max(1, Math.ceil(sortedRequests.length / PAGE_SIZE));

  // Clamp current page if data shrinks (e.g. after a refresh that returned fewer rows).
  useEffect(() => {
    if (page > totalPages - 1) setPage(0);
  }, [page, totalPages]);

  const pagedRequests = useMemo(() => {
    const start = page * PAGE_SIZE;
    return sortedRequests.slice(start, start + PAGE_SIZE);
  }, [sortedRequests, page]);

  const rangeStart = sortedRequests.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, sortedRequests.length);

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-header-content">
          <div className="step-number">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <div className="card-header-text">
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
          {headerHelper && (
            <div className="card-header-helper">{headerHelper}</div>
          )}
        </div>
      </div>
      <div className="card-body">
        <div className="refresh-bar">
          <span className="refresh-info">
            {lastRefresh ? (
              <>Last refreshed: {lastRefresh.toLocaleTimeString()}</>
            ) : (
              'Not yet loaded'
            )}
            {sortedRequests.length > 0 && (
              <>{' · '}Showing {rangeStart}-{rangeEnd} of {sortedRequests.length}</>
            )}
          </span>
          <button
            className="btn btn-secondary btn-small"
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Refreshing...
              </>
            ) : (
              '↻ Refresh'
            )}
          </button>
        </div>

        {sortedRequests.length === 0 && !loading ? (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <p>{emptyMessage}</p>
            <p>{emptyHint}</p>
          </div>
        ) : (
          <>
            <div className="table-container table-container--fit-viewport">
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    <th className="col-req-id">Request ID</th>
                    <th className="col-language">
                      <button type="button" className="th-sort" onClick={() => handleSort('language_pair')}>
                        Language {sortIndicator('language_pair')}
                      </button>
                    </th>
                    <th className="col-tm-header">
                      <button type="button" className="th-sort" onClick={() => handleSort('tm_id')}>
                        TM {sortIndicator('tm_id')}
                      </button>
                    </th>
                    <th className="col-source">
                      <button type="button" className="th-sort" onClick={() => handleSort('source')}>
                        {columnLabels.source} {sortIndicator('source')}
                      </button>
                    </th>
                    <th className="col-target">
                      <button type="button" className="th-sort" onClick={() => handleSort('target')}>
                        {columnLabels.target} {sortIndicator('target')}
                      </button>
                    </th>
                    <th className="col-match-type">
                      <button type="button" className="th-sort" onClick={() => handleSort('translation_type')}>
                        Match Type {sortIndicator('translation_type')}
                      </button>
                    </th>
                    <th className="col-words">
                      <button type="button" className="th-sort" onClick={() => handleSort('words')}>
                        Words {sortIndicator('words')}
                      </button>
                    </th>
                    <th className="col-status">
                      <button type="button" className="th-sort" onClick={() => handleSort('status')}>
                        Status {sortIndicator('status')}
                      </button>
                    </th>
                    <th className="col-date">
                      <button type="button" className="th-sort" onClick={() => handleSort('received_date')}>
                        Date {sortIndicator('received_date')}
                      </button>
                    </th>
                    {showComments && <th>Comments</th>}
                    {showActions && <th className="col-action">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {pagedRequests.map((request) => (
                    <tr
                      key={request.req_id}
                      className={isRequestModified(request) ? 'row-modified' : ''}
                    >
                      <td>
                        <TruncatedCopyCell
                          value={request.req_id || ''}
                          displayText={`${request.req_id?.substring(0, 8) || ''}...`}
                          copyAriaLabel="Copy Request ID"
                          textAs="code"
                          tooltipWrap
                        />
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                        {getLanguagePairLabel(request, true)}
                      </td>
                      <td
                        className="text-wrap col-tm"
                        style={{ fontSize: '0.85rem' }}
                        title={resolveTmId(request, availableTMs)}
                      >
                        {getTmDisplayName(resolveTmId(request, availableTMs), availableTMs) || '—'}
                      </td>
                      <td className="text-wrap col-source">
                        <TruncatedCopyCell
                          value={request.source || request.English || ''}
                          displayText={truncateText(request.source || request.English, 48)}
                          copyAriaLabel="Copy Source"
                          tooltipWrap
                        />
                      </td>
                      <td className="text-wrap col-target">
                        <SpanishCellPopover
                          request={request}
                          displayText={truncateText(request.target || request.Spanish, 48)}
                        />
                      </td>
                      <td>
                        {request.translation_type ? (
                          <span className={`type-badge ${getTypeClass(request.translation_type)}`}>
                            {formatTranslationType(request.translation_type)}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                        {getWordCount(request).toLocaleString()}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <StatusCell request={request} />
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                        {formatDate(request.received_date)}
                      </td>
                      {showComments && (
                        <td className="text-wrap">
                          <TruncatedCopyCell
                            value={request.notes || ''}
                            displayText={request.notes ? truncateText(request.notes, 80) : '-'}
                            copyAriaLabel="Copy Comments"
                            tooltipWrap
                          />
                        </td>
                      )}
                      {showActions && (
                        <td className="col-action">
                          {canReview(request) ? (
                            <div className="action-controls">
                              <button
                                className="btn btn-primary btn-small btn-nowrap"
                                onClick={() => onViewRequest(request)}
                                title="Review and approve translation"
                              >
                                Review Translation
                              </button>
                              <TranslationLockControl
                                resourceType="section"
                                translationId={request.req_id}
                                currentUserId={currentUserId}
                                {...normalizeLockState(request)}
                                onLockStatusChange={(lock) => {
                                  onLockStatusChange?.(request.req_id, lock);
                                }}
                                onNotify={onNotify}
                              />
                            </div>
                          ) : (
                            <span className="action-placeholder">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination-bar">
                <button
                  className="btn btn-ghost btn-small"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  ← Previous
                </button>
                <span className="pagination-info">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  className="btn btn-ghost btn-small"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default RequestsTable;
